import React, { useState, useEffect } from 'react';
import {
  X,
  FileText,
  Users,
  FolderGit2,
  BookOpen,
  CheckSquare,
  User,
  Sparkles,
  Plus,
  ArrowRight,
} from 'lucide-react';

export const NOTE_TEMPLATES = [
  {
    id: 'meeting-notes',
    title: 'Meeting Notes',
    category: 'Work',
    tags: ['meeting', 'work', 'notes'],
    icon: Users,
    color: '#3b82f6',
    description: 'Structure for meeting agenda, attendees, key decisions, and follow-up action items.',
    defaultTitle: 'Meeting Notes: [Project/Topic]',
    content: `# Meeting Notes: [Topic]

**Date:** ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
**Attendees:** @Name, @Name
**Objective:** Align on project deliverables and timeline

---

## 📋 Agenda
1. Review sprint progress
2. Address blockers & architectural choices
3. Next steps & ownership

## 💡 Key Decisions
- Decision 1: Proceed with zero-knowledge AES-256-GCM architecture.
- Decision 2: Target release milestone for next Friday.

## ✅ Action Items
- [ ] Finalize encrypted schema definitions (@Nikhil)
- [ ] Review access control policies (@Security)
- [ ] Configure client-side autosave debounce (@Frontend)
- [ ] Deploy staging verification build (@DevOps)

## 📝 Notes & Discussion
> Key takeaway: Ensure client-side encryption handles attachments without server-side plaintext leakage.
`,
  },
  {
    id: 'project-notes',
    title: 'Project Notes',
    category: 'Projects',
    tags: ['project', 'architecture', 'spec'],
    icon: FolderGit2,
    color: '#8b5cf6',
    description: 'High-level project specification, architecture overview, milestones, and open questions.',
    defaultTitle: 'Project Spec: [Project Name]',
    content: `# Project Spec: [Project Name]

**Status:** In Progress
**Lead:** Security Engineering
**Target Completion:** End of Quarter

---

## 🎯 Executive Summary
Provide a high-level overview of the feature, business value, and technical motivation.

## 🏗️ Architecture & Security
- **Encryption:** Client-side AES-256-GCM authenticated encryption.
- **Key Derivation:** Argon2id with 64MB memory cost.
- **Database:** Encrypted JSON blobs in PostgreSQL.

\`\`\`json
{
  "module": "secure-notes",
  "zeroKnowledge": true,
  "status": "ACTIVE"
}
\`\`\`

## 🚩 Milestones
- [ ] Phase 1: Core encrypted storage & sidebar navigation
- [ ] Phase 2: Rich text editor & attachments
- [ ] Phase 3: Version history & activity audit
- [ ] Phase 4: Production release

## ❓ Open Questions
1. Should note lock PINs derive a separate sub-key?
2. Maximum attachment size for client-side storage?
`,
  },
  {
    id: 'documentation',
    title: 'Documentation',
    category: 'Security',
    tags: ['runbook', 'docs', 'devops'],
    icon: BookOpen,
    color: '#06b6d4',
    description: 'Technical runbook, server operations, emergency failover, or service documentation.',
    defaultTitle: 'Runbook: [Service/System]',
    content: `# Technical Runbook: [Service/System]

**Classification:** CONFIDENTIAL / ZERO-KNOWLEDGE
**Last Audited:** ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}

---

## 🌐 Endpoints & Infrastructure
- **Production API:** \`https://api.ciphervault.internal\`
- **Database Replica:** \`db-primary-us-east.ciphervault.internal:5432\`
- **Auth Provider:** JWT + WebAuthn FIDO2

## 🚨 Incident Response & Failover
1. **Health Verification:** Check \`/health\` cluster endpoint.
2. **Tunnel Verification:** Verify Cloudflare Zero Trust tunnel connectivity.
3. **Database Failover:** If primary fails, promote read replica via Patroni.

\`\`\`bash
# Emergency cluster inspection
curl -s -H "Authorization: Bearer $EMERGENCY_KEY" https://api.ciphervault.internal/health
\`\`\`

## 🔒 Secret Access & Key Rotation
- Master keys are never logged or stored on disk.
- If compromised, execute instant token revocation.
`,
  },
  {
    id: 'checklist',
    title: 'Checklist',
    category: 'Work',
    tags: ['checklist', 'audit', 'compliance'],
    icon: CheckSquare,
    color: '#10b981',
    description: 'Structured deployment checklist, security audit, or release verification list.',
    defaultTitle: 'Checklist: [Release/Audit Name]',
    content: `# Security & Release Checklist

**Release Version:** v2.4.0
**Inspector:** Nikhil Sharma

---

## 🛡️ Pre-Deployment Checks
- [ ] Database migrations executed without locks
- [ ] Zero-knowledge crypto tests passing 100%
- [ ] CORS & Content Security Policy (CSP) headers verified
- [ ] Secrets and master keys excluded from client bundles

## 🚀 Deployment Phase
- [ ] Deploy API services in rolling sequence
- [ ] Deploy Web static assets with cache-busting
- [ ] Verify WebAuthn and 2FA login pipelines

## 🔍 Post-Deployment Verification
- [ ] Audit logs recording events accurately
- [ ] Client-side encryption/decryption latency under 30ms
- [ ] Smoke test notes creation, editing, and trash recovery
`,
  },
  {
    id: 'personal-note',
    title: 'Personal Note',
    category: 'Personal',
    tags: ['personal', 'journal', 'confidential'],
    icon: User,
    color: '#f59e0b',
    description: 'Encrypted personal thoughts, journal entry, or confidential reminders.',
    defaultTitle: 'Personal: [Topic/Date]',
    content: `# Personal Note: [Topic]

**Date:** ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}

---

## 🔒 Confidential Thoughts
Keep private thoughts, reflections, and personal plans securely encrypted with zero-knowledge protection.

## 📌 Goals & Reminders
- [ ] Personal goal 1
- [ ] Personal goal 2
- [ ] Review weekly achievements
`,
  },
  {
    id: 'custom-template',
    title: 'Custom Template',
    category: 'Custom',
    tags: ['custom', 'template'],
    icon: Sparkles,
    color: '#ec4899',
    description: 'Blank formatted scaffold ready for your own specialized structure.',
    defaultTitle: 'Custom: [Title]',
    content: `# [Template Title]

**Created:** ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
**Category:** Custom

---

## Section 1
Enter formatted details here.

## Section 2
- [ ] Checklist item 1
- [ ] Checklist item 2

> Add blockquote or highlight key information here.
`,
  },
];

export default function NoteTemplatesModal({ open, onClose, onSelectTemplate }) {
  const [selectedId, setSelectedId] = useState(NOTE_TEMPLATES[0].id);

  // Lock body scroll and hide bottom dock on mobile
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open', 'template-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open', 'template-modal-open');
    }
    return () => {
      document.body.classList.remove('mobile-modal-open', 'template-modal-open');
    };
  }, [open]);

  if (!open) return null;

  const activeTemplate = NOTE_TEMPLATES.find((t) => t.id === selectedId) || NOTE_TEMPLATES[0];

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-templates-modal note-right-drawer animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Reusable Note Templates"
      >
        <div className="modal-header note-templates-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
              <Sparkles size={20} />
            </div>
            <div className="modal-header-titles">
              <h2 className="modal-title">Reusable Note Templates</h2>
              <p className="modal-subtitle">Select a pre-structured encrypted template to accelerate your notes.</p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="note-templates-body">
          {/* Left list of templates */}
          <div className="note-templates-list">
            {NOTE_TEMPLATES.map((tpl) => {
              const Icon = tpl.icon;
              const isSelected = tpl.id === selectedId;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  className={`note-template-card ${isSelected ? 'active' : ''}`}
                  onClick={() => setSelectedId(tpl.id)}
                >
                  <div
                    className="note-template-icon-box"
                    style={{
                      background: `${tpl.color}18`,
                      color: tpl.color,
                      borderColor: `${tpl.color}30`,
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="note-template-info">
                    <span className="note-template-title">{tpl.title}</span>
                    <span className="note-template-desc">{tpl.description}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right template preview */}
          <div className="note-template-preview-panel">
            <div className="note-template-preview-header">
              <div className="template-badge" style={{ background: `${activeTemplate.color}20`, color: activeTemplate.color }}>
                {activeTemplate.category}
              </div>
              <h3 className="template-preview-title">{activeTemplate.defaultTitle}</h3>
              <div className="template-tags-row">
                {activeTemplate.tags.map((tg) => (
                  <span key={tg} className="note-tag-pill">#{tg}</span>
                ))}
              </div>
            </div>

            <div className="note-template-preview-content">
              <pre className="template-content-code">{activeTemplate.content}</pre>
            </div>

            <div className="note-template-preview-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onSelectTemplate(activeTemplate);
                  onClose();
                }}
              >
                <span>Use This Template</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
